"use client";

import { X, Sun, MapPin, Leaf } from "lucide-react";
import { Style } from "./StylePicker";
import {
  RECIPE_PLACEHOLDER_IMAGE,
  fallbackToRecipePlaceholder,
} from "@/lib/recipePlaceholder";

interface RecipeDetailsModalProps {
  recipe: Style | null;
  isOpen: boolean;
  onClose: () => void;
  onSelect: (recipeId: string) => void;
  isSelected: boolean;
  primaryColor?: string;
  // Shopify plant availability keyed by genusAlias id; missing key = available
  plantAvailability?: Record<string, boolean>;
  // True when fewer than half the recipe's plants are in stock
  soldOut?: boolean;
}

export function RecipeDetailsModal({
  recipe,
  isOpen,
  onClose,
  onSelect,
  isSelected,
  primaryColor = "#10b981",
  plantAvailability,
  soldOut = false,
}: RecipeDetailsModalProps) {
  if (!isOpen || !recipe) return null;

  const formatSunLevel = (sunLevel: string) => {
    return sunLevel
      .replace(/_/g, " ")
      .replace("PART SUN PART SHADE", "Part Sun")
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatZone = (zone: number | string) => {
    const zoneStr = zone.toString();
    return zoneStr.replace(/^ZONE_/i, "");
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackgroundClick}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative bg-white dark:bg-plantista-950 border border-gray-200 dark:border-plantista-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header with close button */}
        <div className="sticky top-0 bg-white dark:bg-plantista-950 border-b border-gray-200 dark:border-plantista-800 p-4 flex justify-between items-center z-10">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold text-plantista-950 dark:text-white font-display">
              {recipe.name}
            </h2>
            {recipe.usableZones && recipe.usableZones.length > 0 && (
              <div className="flex items-center space-x-1 text-sm">
                <MapPin className="w-4 h-4 text-yellow-500" />
                <span className="text-plantista-600 dark:text-plantista-400">
                  {recipe.usableZones.map(formatZone).join(", ")}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-plantista-900 rounded-full transition"
            aria-label="Close"
          >
            <X className="w-6 h-6 text-gray-500 dark:text-plantista-400" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Recipe Image */}
          <div className="aspect-video w-full rounded-lg overflow-hidden bg-gray-100 dark:bg-plantista-900">
            <img
              src={recipe.imageUrl || RECIPE_PLACEHOLDER_IMAGE}
              alt={recipe.name}
              className="w-full h-full object-cover"
              onError={fallbackToRecipePlaceholder}
            />
          </div>

          {/* Recipe Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <div
                className="p-3 rounded-full"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <Sun className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <div>
                <p className="text-sm text-plantista-600 dark:text-plantista-400">
                  Sun Level
                </p>
                <p className="font-semibold text-plantista-950 dark:text-white">
                  {formatSunLevel(recipe.sunLevel)}
                </p>
              </div>
            </div>

            {recipe.usableZones && recipe.usableZones.length > 0 && (
              <div className="flex items-center space-x-3">
                <div
                  className="p-3 rounded-full"
                  style={{ backgroundColor: `${primaryColor}20` }}
                >
                  <MapPin className="w-5 h-5" style={{ color: primaryColor }} />
                </div>
                <div>
                  <p className="text-sm text-plantista-600 dark:text-plantista-400">
                    Hardiness Zones
                  </p>
                  <p className="font-semibold text-plantista-950 dark:text-white">
                    {recipe.usableZones.map(formatZone).join(", ")}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <h3 className="font-semibold text-plantista-950 dark:text-white mb-2">
              Description
            </h3>
            <p className="text-plantista-600 dark:text-plantista-300 leading-relaxed">
              {recipe.description}
            </p>
          </div>

          {/* Included Plants */}
          {recipe.genusAliases && recipe.genusAliases.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Leaf className="w-5 h-5" style={{ color: primaryColor }} />
                <h3 className="font-semibold text-plantista-950 dark:text-white">
                  Included Plants ({recipe.genusAliases.length})
                </h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {recipe.genusAliases.map((plant) => {
                  const plantSoldOut = plantAvailability?.[plant.id] === false;
                  return (
                    <div
                      key={plant.id}
                      className="bg-gray-50 dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-lg overflow-hidden"
                    >
                      <div className="aspect-square bg-gray-200 dark:bg-plantista-800">
                        {plant.imageUrl ? (
                          <img
                            src={plant.imageUrl}
                            alt={plant.alias}
                            className={`w-full h-full object-cover ${plantSoldOut ? "grayscale" : ""}`}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Leaf className="w-8 h-8 text-gray-400 dark:text-plantista-600" />
                          </div>
                        )}
                      </div>
                      <div className="p-2">
                        <p className="text-xs font-medium text-plantista-950 dark:text-white leading-tight line-clamp-2">
                          {plant.alias}
                        </p>
                        {plantSoldOut && (
                          <span className="inline-block mt-0.5 text-[10px] font-medium tracking-wide text-stone-500 dark:text-plantista-400">
                            Sold out
                          </span>
                        )}
                        {plant.price != null && (
                          <p
                            className="text-xs mt-0.5"
                            style={{ color: primaryColor }}
                          >
                            ${plant.price.toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Select Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-plantista-800">
            <button
              onClick={() => {
                if (soldOut) return;
                onSelect(recipe.id);
                onClose();
              }}
              disabled={soldOut}
              className={`px-6 py-3 rounded-lg font-bold transition-all ${
                soldOut
                  ? "bg-gray-200 dark:bg-plantista-800 text-gray-400 dark:text-plantista-600 cursor-not-allowed"
                  : "text-plantista-950"
              }`}
              style={
                soldOut
                  ? undefined
                  : { backgroundColor: isSelected ? "#6b7280" : primaryColor }
              }
            >
              {soldOut
                ? "Sold out"
                : isSelected
                  ? "Selected ✓"
                  : "Select This Style"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
